import { z } from "zod";

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const R2_DELETE_BATCH_LIMIT = 1_000;

export const planSchema = z.enum(["free", "pro"]);
export type Plan = z.infer<typeof planSchema>;

export const retentionDaysSchema = z.union([
  z.literal(1),
  z.literal(3),
  z.literal(7),
  z.literal(14),
  z.literal(21),
  z.literal(30),
  z.literal(90),
  z.literal(180),
]);
export type RetentionDays = z.infer<typeof retentionDaysSchema>;

export const retentionEntitlementDaysSchema = z.union([
  z.literal(7),
  z.literal(21),
  z.literal(30),
  z.literal(90),
  z.literal(180),
]);
export type RetentionEntitlementDays = z.infer<
  typeof retentionEntitlementDaysSchema
>;

export const snapshotObjectKindSchema = z.enum([
  "manifest",
  "board_data",
  "table_export",
  "attachment",
  "archive",
]);
export type SnapshotObjectKind = z.infer<typeof snapshotObjectKindSchema>;

const storageSegmentSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(
    /^[A-Za-z0-9_-]+$/,
    "Storage identifiers may contain only letters, numbers, underscores, and hyphens.",
  );

export interface SnapshotStorageContext {
  accountId: string;
  boardId: string;
  snapshotId: string;
}

export interface AttachmentStorageContext extends SnapshotStorageContext {
  attachmentId: string;
  cardId: string;
}

export interface BoardActivityStorageContext {
  accountId: string;
  actionId: string;
  boardId: string;
}

export const DEFAULT_RETENTION_DAYS: RetentionDays = 7;

export const INCLUDED_RETENTION_DAYS: Readonly<
  Record<Plan, readonly RetentionDays[]>
> = {
  free: [1, 3, 7],
  pro: [1, 3, 7, 14, 21],
};

export const DEFAULT_RETENTION_ENTITLEMENT_DAYS: Readonly<
  Record<Plan, RetentionEntitlementDays>
> = {
  free: 7,
  pro: 21,
};

function segment(value: string): string {
  return storageSegmentSchema.parse(value);
}

function assertValidDate(value: Date, label: string): void {
  if (Number.isNaN(value.getTime())) {
    throw new RangeError(`${label} must be a valid Date.`);
  }
}

export function accountObjectPrefix(accountId: string): string {
  return `v1/accounts/${segment(accountId)}/`;
}

export function snapshotObjectPrefix(context: SnapshotStorageContext): string {
  return `${accountObjectPrefix(context.accountId)}boards/${segment(
    context.boardId,
  )}/snapshots/${segment(context.snapshotId)}/`;
}

export function snapshotManifestKey(context: SnapshotStorageContext): string {
  return `${snapshotObjectPrefix(context)}manifest.json`;
}

export function snapshotBoardDataKey(context: SnapshotStorageContext): string {
  return `${snapshotObjectPrefix(context)}data/board.json`;
}

export function snapshotTableExportKey(
  context: SnapshotStorageContext,
  table: string,
): string {
  return `${snapshotObjectPrefix(context)}data/${segment(table)}.csv`;
}

export function snapshotAttachmentKey(
  context: AttachmentStorageContext,
): string {
  return `${snapshotObjectPrefix(context)}attachments/${segment(
    context.cardId,
  )}/${segment(context.attachmentId)}/content`;
}

export function snapshotAttachmentGenerationKey(
  context: AttachmentStorageContext,
  generationId: string,
): string {
  return `${snapshotObjectPrefix(context)}attachments/${segment(
    context.cardId,
  )}/${segment(context.attachmentId)}/generations/${segment(
    generationId,
  )}/content`;
}

export function isSnapshotAttachmentKey(
  context: AttachmentStorageContext,
  value: string,
): boolean {
  if (value === snapshotAttachmentKey(context)) {
    return true;
  }
  const generationPrefix = `${snapshotObjectPrefix(
    context,
  )}attachments/${segment(context.cardId)}/${segment(
    context.attachmentId,
  )}/generations/`;
  if (!value.startsWith(generationPrefix)) {
    return false;
  }
  const suffix = value.slice(generationPrefix.length);
  const parts = suffix.split("/");
  if (parts.length !== 2 || parts[1] !== "content") {
    return false;
  }
  try {
    return snapshotAttachmentGenerationKey(context, parts[0]!) === value;
  } catch {
    return false;
  }
}

export function snapshotArchiveKey(context: SnapshotStorageContext): string {
  return `${snapshotObjectPrefix(context)}archive/board-preserve.zip`;
}

export function boardActivityObjectKey(
  context: BoardActivityStorageContext,
): string {
  return `${accountObjectPrefix(context.accountId)}boards/${segment(
    context.boardId,
  )}/activity/${segment(context.actionId)}.json`;
}

export function temporaryJobObjectPrefix(
  accountId: string,
  jobId: string,
): string {
  return `v1/tmp/accounts/${segment(accountId)}/jobs/${segment(jobId)}/`;
}

export function isRetentionSelectionAllowed(
  retentionDays: RetentionDays,
  entitlementDays: RetentionEntitlementDays,
): boolean {
  return (
    retentionDaysSchema.parse(retentionDays) <=
    retentionEntitlementDaysSchema.parse(entitlementDays)
  );
}

export function calculateSnapshotExpiry(
  capturedAt: Date,
  retentionDays: RetentionDays = DEFAULT_RETENTION_DAYS,
): Date {
  assertValidDate(capturedAt, "capturedAt");
  const parsedRetentionDays = retentionDaysSchema.parse(retentionDays);

  return new Date(
    capturedAt.getTime() + parsedRetentionDays * DAY_IN_MILLISECONDS,
  );
}

export function isSnapshotExpired(expiresAt: Date, now: Date): boolean {
  assertValidDate(expiresAt, "expiresAt");
  assertValidDate(now, "now");

  return expiresAt.getTime() <= now.getTime();
}

export function chunkR2DeleteKeys(
  keys: readonly string[],
): readonly string[][] {
  const batches: string[][] = [];

  for (let index = 0; index < keys.length; index += R2_DELETE_BATCH_LIMIT) {
    batches.push(keys.slice(index, index + R2_DELETE_BATCH_LIMIT));
  }

  return batches;
}
